import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';

// ~20 Noten, die chaotisch durch den Hero treiben — steht für Kreativität.
// Jede Note bekommt eigene Größe, Geschwindigkeit, Drift und Rotation.
const chars = ['♪', '♫', '♩', '♬', '♭', '♮'];
const sizes = ['text-base', 'text-lg', 'text-xl', 'text-2xl', 'text-3xl', 'text-4xl'];
const rand = (min, max) => Math.random() * (max - min) + min;

const notes = Array.from({ length: 20 }, (_, i) => ({
  id: i,
  left: `${rand(2, 94)}%`,            // Startposition horizontal
  char: chars[Math.floor(rand(0, chars.length))],
  size: sizes[Math.floor(rand(0, sizes.length))],
  delay: rand(0, 12),                 // versetzter Start
  dur: rand(8, 20),                   // verschiedene Geschwindigkeiten
  driftX: rand(-120, 120),            // seitliches Driften (px)
  rise: rand(-50, -85),               // wie hoch es steigt (vh)
  rot: rand(-40, 40),                 // leichte Rotation
  opacity: rand(0.25, 0.55),          // unterschiedliche Deckkraft
}));

const Hero = () => {
  const reduceMotion = useReducedMotion();

  return (
    <section id="hero" className="relative h-[80vh] flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0 z-0">
        <img
          src="/images/hero-music.svg"
          alt="Lyrik und Musik"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-brand/70 dark:bg-navy-950/80 mix-blend-multiply dark:mix-blend-normal transition-colors duration-300"></div>
      </div>

      {/* Dezent aufsteigende Noten (zwischen Hintergrund und Text) */}
      {!reduceMotion && (
        <div className="absolute inset-0 z-[5] pointer-events-none overflow-hidden" aria-hidden="true">
          {notes.map((n) => (
            <motion.span
              key={n.id}
              className={`absolute bottom-0 text-amber-300 dark:text-amber-400 ${n.size} select-none`}
              style={{ left: n.left }}
              initial={{ y: 0, x: 0, opacity: 0, rotate: 0 }}
              animate={{
                y: [`0vh`, `${n.rise * 0.5}vh`, `${n.rise}vh`],
                x: [0, n.driftX, n.driftX * 0.3, n.driftX * 0.8],
                rotate: [0, n.rot, -n.rot * 0.5, n.rot],
                opacity: [0, n.opacity, n.opacity, 0],
              }}
              transition={{
                duration: n.dur,
                delay: n.delay,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            >
              {n.char}
            </motion.span>
          ))}
        </div>
      )}
      <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-amber-300 dark:text-amber-400 text-sm font-semibold tracking-widest uppercase mb-4"
        >
          Song-Konzepte aus zeitloser Lyrik
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="text-4xl md:text-6xl lg:text-7xl font-serif font-bold text-white mb-6"
        >
          Lyrik im Wohlklang
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.25 }}
          className="text-lg md:text-2xl text-stone-200 mb-10 font-light max-w-2xl mx-auto"
        >
          Liedzeit verwandelt Poesie von Heine, Hesse und Padel in Song-Konzepte,
          produziert mit KI und DAW – als Anregung, nicht als fertiges Produkt
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="flex flex-wrap items-center justify-center gap-4"
        >
          <button
            onClick={() => document.getElementById('konzepte')?.scrollIntoView({ behavior: 'smooth' })}
            className="bg-amber-700 hover:bg-amber-800 text-white px-8 py-3 rounded-sm text-lg transition-all duration-300 shadow-lg"
          >
            Song-Konzepte entdecken
          </button>
          <a
            href="https://radio.liedzeit.de/public/radio_liedzeit"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-amber-700 hover:bg-amber-800 text-white px-8 py-3 rounded-sm text-lg transition-all duration-300 shadow-lg"
          >
            Radio Liedzeit
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
